import React, { useState, useMemo } from 'react';
import { ArrowUpRight, ArrowDownLeft, Search, Filter, XCircle, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { usePlatform } from '../../context/PlatformContext';

const STATUS_CONFIG = {
  completed: { label: 'Completed', icon: CheckCircle2, bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  failed: { label: 'Failed', icon: XCircle, bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  pending: { label: 'Pending', icon: Clock, bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'credit', label: 'Deposits' },
  { key: 'debit', label: 'Transfers' },
  { key: 'failed', label: 'Failed' },
];

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-NG', { day: 'numeric', month: 'short' });
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-NG', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function PlatformTransactions() {
  const { transactions, transactionsTotal, stats, loaded } = usePlatform();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);

  const filtered = useMemo(() => {
    let list = transactions;
    if (filter === 'credit') list = list.filter(t => t.type === 'credit');
    else if (filter === 'debit') list = list.filter(t => t.type === 'debit' && t.status !== 'failed');
    else if (filter === 'failed') list = list.filter(t => t.status === 'failed');
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        t.reference?.toLowerCase().includes(q) ||
        t.wallet_user_name?.toLowerCase().includes(q) ||
        t.payout_account_name?.toLowerCase().includes(q) ||
        t.payout_bank_name?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [transactions, filter, search]);

  const v = stats?.volume || {};
  const t = stats?.transactions || {};
  const summaryCards = [
    { label: 'Total Volume', value: `₦${Number(v.total_inflow_ngn || 0).toLocaleString()}`, sub: `${transactionsTotal} transactions` },
    { label: 'Deposits', value: transactions.filter(tx => tx.type === 'credit').length, sub: 'inbound payments' },
    { label: 'Transfers', value: transactions.filter(tx => tx.type === 'debit' && tx.status === 'completed').length, sub: 'outbound payments' },
    { label: 'Failed', value: t.failed ?? transactions.filter(tx => tx.status === 'failed').length, sub: 'need attention' },
  ];

  return (
    <div data-testid="platform-transactions-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Transactions</h1>
        <p className="text-gray-500 mt-1">All financial activity across your platform</p>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {summaryCards.map(card => (
          <div key={card.label} className="bg-white rounded-xl border border-gray-200 px-4 py-3" data-testid={`txn-stat-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <p className="text-xs text-gray-500 mb-0.5">{card.label}</p>
            <p className="text-lg font-bold text-gray-900">{loaded ? card.value : '—'}</p>
            <p className="text-xs text-gray-400">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Filters + Search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1" data-testid="txn-filters">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filter === f.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {f.label}
              {f.key === 'failed' && transactions.filter(t => t.status === 'failed').length > 0 && (
                <span className="ml-1 w-4 h-4 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px]">
                  {transactions.filter(t => t.status === 'failed').length}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="relative flex-1 w-full sm:w-auto sm:max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            data-testid="txn-search-input"
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E8678B]/20 focus:border-[#E8678B]"
            placeholder="Search reference, name, bank..." />
        </div>
      </div>

      {/* Transaction list */}
      {!loaded ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-testid="txn-empty">
          <Filter size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 text-sm">{search || filter !== 'all' ? 'No transactions match your filters' : 'No transactions yet'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="txn-list">
          {/* Table header */}
          <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-4">Transaction</div>
            <div className="col-span-2">Wallet</div>
            <div className="col-span-2 text-right">Amount</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Time</div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-100">
            {filtered.map(txn => {
              const isCredit = txn.type === 'credit';
              const sc = STATUS_CONFIG[txn.status] || STATUS_CONFIG.pending;
              const isExpanded = expanded === txn.id;

              return (
                <div key={txn.id} data-testid={`txn-row-${txn.id}`}>
                  <div
                    className="grid grid-cols-12 gap-4 px-5 py-3.5 items-center hover:bg-gray-50/60 cursor-pointer transition-colors"
                    onClick={() => setExpanded(isExpanded ? null : txn.id)}
                  >
                    {/* Transaction info */}
                    <div className="col-span-12 sm:col-span-4 flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${isCredit ? 'bg-green-50' : 'bg-orange-50'}`}>
                        {isCredit
                          ? <ArrowDownLeft size={16} className="text-green-600" />
                          : <ArrowUpRight size={16} className="text-orange-600" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {isCredit ? 'Deposit via bank transfer' : `Transfer to ${txn.payout_account_name || 'bank'}`}
                        </p>
                        <p className="text-xs text-gray-400 font-mono truncate">{txn.reference}</p>
                      </div>
                    </div>

                    {/* Wallet */}
                    <div className="hidden sm:block col-span-2">
                      <p className="text-sm text-gray-700 truncate">{txn.wallet_user_name}</p>
                      <p className="text-xs text-gray-400">{txn.wallet_user_id}</p>
                    </div>

                    {/* Amount */}
                    <div className="hidden sm:block col-span-2 text-right">
                      <p className={`text-sm font-semibold ${isCredit ? 'text-green-600' : 'text-gray-900'}`}>
                        {isCredit ? '+' : '-'}₦{Number(txn.amount).toLocaleString()}
                      </p>
                      {parseFloat(txn.fee) > 0 && (
                        <p className="text-xs text-gray-400">fee: ₦{Number(txn.fee).toLocaleString()}</p>
                      )}
                    </div>

                    {/* Status */}
                    <div className="hidden sm:block col-span-2">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {sc.label}
                      </span>
                    </div>

                    {/* Time */}
                    <div className="hidden sm:block col-span-2 text-right">
                      <p className="text-sm text-gray-500">{timeAgo(txn.created_at)}</p>
                    </div>

                    {/* Mobile: amount + status */}
                    <div className="col-span-12 sm:hidden flex items-center justify-between mt-1">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {sc.label}
                      </span>
                      <p className={`text-sm font-semibold ${isCredit ? 'text-green-600' : 'text-gray-900'}`}>
                        {isCredit ? '+' : '-'}₦{Number(txn.amount).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-5 pb-4 bg-gray-50/50 border-t border-gray-100" data-testid={`txn-detail-${txn.id}`}>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-3">
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Reference</p>
                          <p className="text-xs font-mono text-gray-700">{txn.reference}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Type</p>
                          <p className="text-xs text-gray-700 capitalize">{txn.category?.replace('_', ' ')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Date & Time</p>
                          <p className="text-xs text-gray-700">{formatDate(txn.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 mb-0.5">Wallet</p>
                          <p className="text-xs text-gray-700">{txn.wallet_user_name} ({txn.wallet_user_id})</p>
                        </div>
                        {txn.payout_bank_name && (
                          <>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Destination Bank</p>
                              <p className="text-xs text-gray-700">{txn.payout_bank_name}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Account</p>
                              <p className="text-xs text-gray-700">{txn.payout_account_name} &middot; {txn.payout_account_number}</p>
                            </div>
                          </>
                        )}
                        {txn.failure_reason && (
                          <div className="col-span-2">
                            <p className="text-xs text-gray-400 mb-0.5">Failure Reason</p>
                            <div className="flex items-center gap-1.5">
                              <AlertTriangle size={12} className="text-red-500" />
                              <p className="text-xs text-red-600">{txn.failure_reason}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Showing {filtered.length} of {transactionsTotal} transactions
            </p>
            {filtered.length < transactionsTotal && (
              <p className="text-xs text-gray-400">Scroll for more or refine your search</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
